import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Stack,
  Text,
  Badge,
  Group,
  Title,
  Divider,
  Box,
  Loader,
  Alert,
  Button,
  Textarea,
  ScrollArea,
  ThemeIcon,
  Breadcrumbs,
  Anchor,
  Table,
  TextInput,
  Select,
  Modal,
  Tooltip,
  ActionIcon

} from '@mantine/core';
import {
  IconDatabase,
  IconArrowsMaximize,
  IconAlertCircle,
  IconDeviceFloppy,
  IconCode,
  IconTableColumn,
  IconCheck,
  IconX,
  IconBrandGithub,
  IconChevronRight,
  IconPlayerPlayFilled,
  IconClock,
  IconBulb,
  IconRowsPlus
} from '@tabler/icons-react';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import useAuthenticatedPut from '../../hooks/useAuthenticatedPut';
import config from '../../config';
import SqlEditor from './LazySqlEditor';

const QueryEditor = () => {
  const [searchParams] = useSearchParams();
  const queryKey = searchParams.get('queryKey');
  const isAdmin = searchParams.get('admin') === 'true';
  const featureId = searchParams.get('featureId');

  const [queryContent, setQueryContent] = useState('');
  const [args, setArgs] = useState([]);
  const [expectedColumns, setExpectedColumns] = useState([]);
  const [queryMetadata, setQueryMetadata] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [autoError, setAutoError] = useState(null);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  // The 3-column layout caps how big the editor can get; this lifts that ceiling.
  const [sqlFullscreen, setSqlFullscreen] = useState(false);

  // Query execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [executionError, setExecutionError] = useState(null);
  const [argumentValues, setArgumentValues] = useState({});

  const { post, isLoading: isCreating } = useAuthenticatedPost();
  const { put } = useAuthenticatedPut();

  // counter to bust cache after creation
  const [reloadCounter, setReloadCounter] = useState(0);

  // main query fetch
  const queryUrl = queryKey
    ? `${config.apiBaseUrl}/queries/query/${encodeURIComponent(queryKey)}?r=${reloadCounter}`
    : null;
  const {
    data: queryData,
    isLoading: loading,
    error,
    refetch,
  } = useAuthenticatedData(queryUrl);

  // arguments fetch
  const argsUrl = queryKey
    ? `${config.apiBaseUrl}/queries/query/${encodeURIComponent(queryKey)}/arguments?r=${reloadCounter}`
    : null;
  const {
    data: argsData,
    isLoading: loadingArgs,
    error: argsError,
    refetch: refetchArgs,
  } = useAuthenticatedData(argsUrl);

  // columns fetch
  const colsUrl = queryKey
    ? `${config.apiBaseUrl}/queries/query/${encodeURIComponent(queryKey)}/columns?r=${reloadCounter}`
    : null;
  const {
    data: colsData,
    isLoading: loadingCols,
    error: colsError,
    refetch: refetchCols,
  } = useAuthenticatedData(colsUrl);

  // auto-create on 404
  useEffect(() => {
    if (queryKey && featureId && error?.status === 404 && !isCreating) {
      post(
        `${config.apiBaseUrl}/queries/query/new-default?featureId=${featureId}`
      )
        .then(() => {
          if (refetch) refetch();
          if (refetchArgs) refetchArgs();
          if (refetchCols) refetchCols();
          setReloadCounter((c) => c + 1);
        })
        .catch(console.error);
    }
  }, [error, queryKey, featureId, post, refetch, refetchArgs, refetchCols, isCreating]);

  // seed SQL, metadata, breadcrumbs
  useEffect(() => {
    if (queryData) {
      setQueryContent(queryData.sql || '');
      setQueryMetadata(queryData.metadata || {});
      setBreadcrumbs(queryData.breadcrumbs || []);
    }
  }, [queryData]);

  // seed args and initialize argument values
  useEffect(() => {
    if (Array.isArray(argsData)) {
      setArgs(argsData);
      // Initialize argument values
      const initialValues = {};
      argsData.forEach(arg => {
        initialValues[arg.name] = '';
      });
      setArgumentValues(initialValues);
    } else if (argsData?.arguments) {
      setArgs(argsData.arguments);
      // Initialize argument values
      const initialValues = {};
      argsData.arguments.forEach(arg => {
        initialValues[arg.name] = '';
      });
      setArgumentValues(initialValues);
    }
  }, [argsData]);

  // seed columns
  useEffect(() => {
    if (Array.isArray(colsData)) {
      setExpectedColumns(colsData);
    } else if (colsData?.columns) {
      setExpectedColumns(colsData.columns);
    }
  }, [colsData]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveResult(null);
    try {
      const result = await put(
        `${config.apiBaseUrl}/queries/query/${encodeURIComponent(queryKey)}`,
        { sql: queryContent, args, expectedColumns }
      );
      // result = { success, message, validationErrors, hasError, ... }
      setSaveResult({
        hasError: result.hasError,
        message: result.message,
        validationErrors: result.validationErrors
      });
      // clear after a bit
      setTimeout(() => setSaveResult(null), 5000);
    }
    catch (err) {
      console.error('Save error:', err);
      setSaveResult({ hasError: true, message: err.message || 'Save failed' });
    } finally {
      setIsSaving(false);
    }
  };

  // In QueryEditor component, update the handleAutoPopulate function:

  const handleAutoPopulate = async () => {
    setAutoError(null);
    setIsAutoPopulating(true);

    try {
      // Send the current argument values to the backend
      const result = await post(
        `${config.apiBaseUrl}/queries/query/${encodeURIComponent(queryKey)}/auto-populate`,
        argumentValues  // Pass the current argument values from the form
      );

      // Refresh data from server to get the newly populated columns and arguments
      if (refetchArgs) refetchArgs();
      if (refetchCols) refetchCols();

      // Bust cache counter to force fresh data
      setReloadCounter((c) => c + 1);

    } catch (err) {
      console.error('Auto-populate error:', err);
      setAutoError(err.message || 'Failed to auto-populate');
    } finally {
      setIsAutoPopulating(false);
    }
  };

  const handleExecuteQuery = async () => {
    setIsExecuting(true);
    setExecutionError(null);
    setExecutionResult(null);

    try {
      const result = await post(
        `${config.apiBaseUrl}/queries/execute?queryKey=${encodeURIComponent(queryKey)}`,
        argumentValues
      );
      setExecutionResult(result);
    } catch (err) {
      console.error('Execution error:', err);
      setExecutionError(err.message || 'Failed to execute query');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleArgumentChange = (argName, value) => {
    setArgumentValues(prev => ({
      ...prev,
      [argName]: value
    }));
  };

  const renderArgumentInput = (arg) => {
    const value = argumentValues[arg.name] || '';

    switch (arg.type?.toLowerCase()) {
      case 'int':
      case 'integer':
      case 'number':
        return (
          <TextInput
            type="number"
            value={value}
            onChange={(e) => handleArgumentChange(arg.name, e.currentTarget.value)}
            placeholder={`Enter ${arg.name}`}
            size="xs"
          />
        );
      case 'bool':
      case 'boolean':
        return (
          <Select
            value={value}
            onChange={(val) => handleArgumentChange(arg.name, val)}
            data={[
              { value: 'true', label: 'True' },
              { value: 'false', label: 'False' }
            ]}
            placeholder="Select true/false"
            size="xs"
          />
        );
      case 'json':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleArgumentChange(arg.name, e.currentTarget.value)}
            placeholder="Enter JSON"
            size="xs"
            minRows={2}
            maxRows={4}
          />
        );
      default:
        return (
          <TextInput
            value={value}
            onChange={(e) => handleArgumentChange(arg.name, e.currentTarget.value)}
            placeholder={`Enter ${arg.name}`}
            size="xs"
          />
        );
    }
  };

  const renderExecutionResults = () => {
    if (executionError) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          <Text  c="red" fw={500} mb="xs">Execution Error</Text>
          <Text    size="sm">{executionError}</Text>
        </Alert>
      );
    }

    if (!executionResult) {
      return (
        <Box p="xl" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px', border: '2px dashed var(--mantine-color-gray-4)' }}>
          <Text size="sm"      ta="center">Execute query to see results here</Text>
        </Box>
      );
    }

    const { success, data, rowCount, executionTimeMs, errors, metadata } = executionResult;

    if (!success) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          <Text fw={500} mb="xs">Query Failed</Text>
          {errors && errors.length > 0 && (
            <Stack gap="xs">
              {errors.map((error, idx) => (
                <Text    key={idx} size="sm">{error}</Text>
              ))}
            </Stack>
          )}
        </Alert>
      );
    }

    return (
      <Stack gap="md">
        <Group gap="md">
          <Group gap="md">
            <IconTableColumn size={16} />
            <Text    size="sm" fw={500}>{rowCount} rows</Text>
          </Group>
          <Group gap="xs">
            <IconClock size={16} />
            <Text    size="sm" fw={500}>{executionTimeMs}ms</Text>
          </Group>
          {metadata?.executedAt && (
            <Text size="sm"     >
              Executed at {new Date(metadata.executedAt).toLocaleString()}
            </Text>
          )}
        </Group>

        {data && data.length > 0 && (
          <ScrollArea style={{ maxHeight: '400px' }}>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <thead>
                <tr>
                  {Object.keys(data[0]).map(column => (
                    <th  key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx}>
                    {Object.values(row).map((value, i) => (
                      <td  key={i}>{value == null ? <em>NULL</em> : String(value)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        )}

        {(!data || data.length === 0) && (
          <Text size="sm"      ta="center">No data returned</Text>
        )}
      </Stack>
    );
  };

  // early guards
  if (!queryKey) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          <Text>No query key provided. Please access this page from the Query Commander explorer.</Text>
        </Alert>
      </Container>
    );
  }
  if (loading) {
    return (
      <Container size="xl" py="md">
        <Paper p="md">
          <Group gap="xs">
            <Loader size="sm" />
            <Text>Loading Query...</Text>
          </Group>
        </Paper>
      </Container>
    );
  }
  if (error && error.status !== 404) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Error loading query: {error.message || String(error)}
        </Alert>
      </Container>
    );
  }
  if (!queryData) {
    return (
      <Container size="xl" py="md">
        <Paper p="md">
          <Group gap="xs">
            <Loader size="sm" />
            <Text>Preparing new query…</Text>
          </Group>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Paper p="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="xs">
                <IconDatabase size={24} />
                <Title  order={2}>Query Editor</Title>
                <Badge color="blue" variant="light">{queryKey}</Badge>
                {queryMetadata?.type && <Badge color="green" variant="light">{queryMetadata.type}</Badge>}
              </Group>
              <Group gap="xs">
                <Button
                  leftSection={<IconPlayerPlayFilled size={16} />}
                  onClick={handleExecuteQuery}
                  loading={isExecuting}
                  color="green"
                  disabled={!queryContent.trim()}
                >
                  Execute Query
                </Button>
                {isAdmin && (
                  <Button
                    leftSection={<IconTableColumn size={16} />}
                    onClick={handleAutoPopulate}
                    loading={isAutoPopulating}
                    color={autoError ? 'red' : 'gray'}
                  >
                    Auto-Populate Columns
                  </Button>
                )}
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSave}
                  loading={isSaving}
                  color={saveResult === 'success' ? 'green' : saveResult === 'error' ? 'red' : 'blue'}
                >
                  {saveResult === 'success' ? 'Saved!' : saveResult === 'error' ? 'Error' : 'Save'}
                </Button>
              </Group>
            </Group>

            {breadcrumbs.length > 0 && (
              <Box>
                <Text size="sm" fw={500} mb="xs">Location:</Text>
                <Breadcrumbs separator={<IconChevronRight size={12} />}>
                  {breadcrumbs.map((crumb, i) => (
                    <Anchor key={i} size="sm"     >{crumb}</Anchor>
                  ))}
                </Breadcrumbs>
              </Box>
            )}
            {queryMetadata?.name && (
              <Box>
                <Text size="sm" fw={500}>Query Name:</Text>
                <Text size="sm"     >{queryMetadata.name}</Text>
              </Box>
            )}
            {queryMetadata?.description && (
              <Box>
                <Text size="sm" fw={500}>Description:</Text>
                <Text size="sm"     >{queryMetadata.description}</Text>
              </Box>
            )}
          </Stack>
        </Paper>

        <Grid>
          <Grid.Col span={2}>
            <Paper p="md" withBorder h="82vh">
              <Stack gap="md">

                <Group gap="xs">
                  <IconBulb size={20} />
                  <Title    order={4}>Status</Title>

                  {/* Save result alert */}
                  {saveResult && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color={saveResult.hasError ? 'yellow' : 'green'}
                      variant="light"
                      mb="md"
                    >
                      <Text fw={500}>{saveResult.message}</Text>
                      {saveResult.validationErrors?.length > 0 && (
                        <Stack spacing={2} mt="xs">
                          {saveResult.validationErrors.map((err, i) => (
                            <Text key={i} size="sm">• {err}</Text>
                          ))}
                        </Stack>
                      )}
                    </Alert>
                  )}
                </Group>

                <Group gap="xs">
                  <IconCode size={20} />
                  <Title    order={4}>Arguments</Title>
                  <Badge size="sm" variant="light">{args.length}</Badge>
                </Group>
                <Text size="xs"     >Each query defines 0 or more arguments. Fill in values to execute.</Text>
                <Divider />
                <ScrollArea style={{ height: 'calc(82vh - 140px)' }}>
                  {loadingArgs ? (
                    <Loader size="sm" />
                  ) : args.length === 0 ? (
                    <Text size="sm"      ta="center">No arguments defined</Text>
                  ) : (
                    <Stack gap="sm">
                      {args.map((arg, idx) => (
                        <Paper key={idx} p="sm" withBorder>
                          <Stack gap="xs">
                            <Group gap="xs">
                              <Text      size="sm" fw={500}>{`{${arg.name}}`}</Text>
                              <Badge size="xs" color="blue">{arg.type}</Badge>
                              {arg.required && <Badge size="xs" color="red" variant="light">Required</Badge>}
                            </Group>
                            {arg.description && <Text size="xs"     >{arg.description}</Text>}
                            {renderArgumentInput(arg)}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </ScrollArea>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={8}>
            <Paper p="md" withBorder h="82vh">
              <Stack gap="md">
                <Group gap="xs" justify="space-between" wrap="nowrap">
                  <Group gap="xs"><IconDatabase size={20} /><Title order={4}>SQL Query</Title></Group>
                  <Tooltip label="Expand to full screen">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => setSqlFullscreen(true)}
                      aria-label="Expand SQL editor to full screen"
                    >
                      <IconArrowsMaximize size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text size="xs"     >Enter your raw SQL here. Arguments are denoted like {"{period}"}. Don't include "use" prembles or "go".</Text>
                <Divider />
                <SqlEditor
                  value={queryContent}
                  onChange={setQueryContent}
                  height="calc(82vh - 150px)"
                />
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={2}>
            <Paper p="md" withBorder h="82vh">
              <Stack gap="md">
                <Group gap="xs">
                  <IconTableColumn size={20} />
                  <Title order={4}   >Expected Columns</Title>
                  <Badge size="sm" variant="light">{expectedColumns.length}</Badge>
                </Group>
                <Text size="xs"     >Each column must exist in the SQL output.</Text>
                <Divider />
                <ScrollArea style={{ height: 'calc(82vh - 140px)' }}>
                  {loadingCols ? (
                    <Loader size="sm" />
                  ) : expectedColumns.length === 0 ? (
                    <Text size="sm"      ta="center">No expected columns defined</Text>
                  ) : (
                    <Stack gap="xs">
                      {expectedColumns.map((col, idx) => {
                        const isPresent = queryContent.includes(col.name);
                        return (
                          <Paper key={idx} p="sm" withBorder>
                            <Group gap="xs" justify="space-between">
                              <Box style={{ flex: 1 }}>
                                <Group gap="xs">
                                  <Text    size="sm" fw={500}>{col.name}</Text>
                                  {col.type && <Badge size="xs" color="gray">{col.type}</Badge>}
                                </Group>
                                {col.description && <Text size="xs"     >{col.description}</Text>}
                              </Box>
                              <ThemeIcon size="sm" color={isPresent ? 'green' : 'red'} variant="light">
                                {isPresent ? <IconCheck size={12} /> : <IconX size={12} />}
                              </ThemeIcon>
                            </Group>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </ScrollArea>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        <Paper p="md" withBorder>
          <Stack gap="md">
            <Group gap="xs">
              <IconBrandGithub size={20} />
              <Title    order={4}>Query Results</Title>
              {isExecuting && <Loader size="sm" />}
            </Group>
            <Text size="sm"     >Execute query to see results here.</Text>
            {renderExecutionResults()}
          </Stack>
        </Paper>
      </Stack>

      {/* Same state as the inline editor, so edits made here are already saved when you close it —
          there is no separate buffer to reconcile or accidentally discard. */}
      <Modal
        opened={sqlFullscreen}
        onClose={() => setSqlFullscreen(false)}
        fullScreen
        title={
          <Group gap="xs">
            <IconDatabase size={20} />
            <Title order={4}>SQL Query</Title>
            {queryKey && <Badge variant="light">{queryKey}</Badge>}
          </Group>
        }
      >
        <SqlEditor
          value={queryContent}
          onChange={setQueryContent}
          height="calc(100vh - 130px)"
        />
      </Modal>
    </Container>
  );
};

export default QueryEditor;